import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { API_BASE_URL } from "../components/TokenChecker";

// Small inline modal to prompt token refresh
function TokenExpiryModal({ visible, onRefresh, onLogout, secondsLeft, reason = 'idle' }) {
  if (!visible) return null;
  const secs = Math.max(0, Math.floor((secondsLeft || 0) / 1000));
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
    }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 20, width: 360, boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin: 0, fontSize: 18 }}>
          {reason === 'idle' ? 'You were inactive' : 'Session expiring soon'}
        </h3>
        <p style={{ marginTop: 8, color: '#475569' }}>
          {reason === 'idle'
            ? `To continue, refresh your session${secs ? ` (expires in ~${secs}s)` : ''}.`
            : `Your session will expire${secs ? ` in ~${secs}s` : ' shortly'}. Refresh now to stay signed in.`}
        </p>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onLogout} style={{
            padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff'
          }}>Logout</button>
          <button onClick={onRefresh} style={{
            padding: '8px 12px', borderRadius: 8, border: '1px solid #0ea5e9', background: '#38bdf8', color: '#fff'
          }}>Refresh session</button>
        </div>
      </div>
    </div>
  );
}


const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // Inactivity threshold before prompting manual refresh
  const IDLE_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes

  // base64url decode helper
  const decodeJwt = (token) => {
    try {
      const [, payload] = token.split('.');
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const json = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(json);
    } catch (e) { return null; }
  };

  const readFromStorage = () => {
    // Prefer persistent storage; fallback to session for non-remembered logins
    const storages = [localStorage, sessionStorage];
    for (const s of storages) {
      const access = s.getItem("accessToken");
      const refresh = s.getItem("refreshToken");
      const email = s.getItem("email");
      const firstName = s.getItem("firstName");
      const lastName = s.getItem("lastName");
      if (access && refresh && email) {
        return { accessToken: access, refreshToken: refresh, email, firstName, lastName };
      }
    }
    return {};
  };

  const [authData, setAuthData] = useState(readFromStorage);
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptReason, setPromptReason] = useState('idle');
  const [msUntilExpiry, setMsUntilExpiry] = useState(null);
  const logoutTimerRef = useRef(null);
  const idleTimerRef = useRef(null);
  const lastActiveRef = useRef(Date.now());
  const isAuthed = Boolean(authData?.accessToken && authData?.refreshToken);

  const writeToStorage = (remember, key, value) => {
    const target = remember ? localStorage : sessionStorage;
    target.setItem(key, value);
  };

  const clearStorages = () => {
    [localStorage, sessionStorage].forEach((s) => {
      s.removeItem("accessToken");
      s.removeItem("refreshToken");
      s.removeItem("email");
      s.removeItem("firstName");
      s.removeItem("lastName");
    });
  };

  const login = (email, accessToken, refreshToken, firstName, lastName, remember = true) => {
    clearStorages();
    [
      ["accessToken", accessToken],
      ["refreshToken", refreshToken],
      ["email", email],
      ["firstName", firstName || ""],
      ["lastName", lastName || ""],
    ].forEach(([k, v]) => writeToStorage(remember, k, v));

    setAuthData({ accessToken, refreshToken, email, firstName, lastName });
  };

  const updateProfile = ({ email, firstName, lastName } = {}) => {
    const updates = {};
    if (typeof email === "string") updates.email = email;
    if (typeof firstName === "string") updates.firstName = firstName;
    if (typeof lastName === "string") updates.lastName = lastName;

    if (Object.keys(updates).length === 0) return;

    [localStorage, sessionStorage].forEach((s) => {
      if (updates.email !== undefined) s.setItem("email", updates.email);
      if (updates.firstName !== undefined) s.setItem("firstName", updates.firstName || "");
      if (updates.lastName !== undefined) s.setItem("lastName", updates.lastName || "");
    });

    setAuthData((prev) => ({ ...prev, ...updates }));
  };


  const logout = async () => {
    try {
      const access = authData?.accessToken || localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      const refresh = authData?.refreshToken || localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
      if (access) {
        // Best-effort server-side logout logging + refresh blacklist
        await fetch(`${API_BASE_URL}/api/logout/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${access}`,
          },
          body: JSON.stringify({ refresh }),
        }).catch(() => {});
      }
    } catch (_) {}
    clearStorages();
    setAuthData({});
    setShowPrompt(false);
    setMsUntilExpiry(null);
    // Best-effort redirect if currently on an authed route
    try { window.location.assign('/login'); } catch (_) {}
  };

  const refreshAccess = async () => {
    const refreshToken = authData?.refreshToken || localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
    if (!refreshToken) {
      logout();
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/token/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: refreshToken })
      });
      if (!res.ok) throw new Error('refresh failed');
      const data = await res.json();
      const newAccess = data.access;

      // persist access alongside where refresh lives
      if (localStorage.getItem('refreshToken') === refreshToken) {
        localStorage.setItem('accessToken', newAccess);
      } else if (sessionStorage.getItem('refreshToken') === refreshToken) {
        sessionStorage.setItem('accessToken', newAccess);
      } else {
        localStorage.setItem('accessToken', newAccess);
      }
      setAuthData((prev) => ({ ...prev, accessToken: newAccess }));
      setShowPrompt(false);
      scheduleTimers(newAccess);
      // reset activity on manual refresh
      markActive();
    } catch (e) {
      logout();
    }
  };

  const clearTimers = () => {
    if (logoutTimerRef.current) { clearTimeout(logoutTimerRef.current); logoutTimerRef.current = null; }
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
  };

  const scheduleTimers = (accessToken) => {
    clearTimers();
    if (!accessToken) return;
    const payload = decodeJwt(accessToken);
    const expSec = payload?.exp; // seconds since epoch
    if (!expSec) return;
    const nowMs = Date.now();
    const expMs = expSec * 1000;
    const untilExp = expMs - nowMs;
    if (untilExp <= 0) {
      logout();
      return;
    }

    // store ms until expiry (for UI countdown)
    setMsUntilExpiry(untilExp);

    // schedule hard logout at expiry
    logoutTimerRef.current = setTimeout(() => {
      logout();
    }, untilExp);
  };

  useEffect(() => {
    scheduleTimers(authData?.accessToken);
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.accessToken]);

  // ---- Inactivity tracking ----
  const activityKeys = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'visibilitychange'];
  const markActive = () => {
    if (!isAuthed) return;
    const now = Date.now();
    lastActiveRef.current = now;
    try { localStorage.setItem('flowman:lastActive', String(now)); } catch (_) {}
    // reset idle timer
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      // show manual refresh prompt on idle
      setPromptReason('idle');
      setShowPrompt(true);
    }, IDLE_THRESHOLD_MS);
  };

  useEffect(() => {
    if (!isAuthed) {
      clearTimers();
      setShowPrompt(false);
      setMsUntilExpiry(null);
      return;
    }
    // initialize on mount
    markActive();
    const handler = (e) => {
      if (e.type === 'visibilitychange' && document.visibilityState !== 'visible') return;
      // If already showing prompt (idle), keep it visible until the user refreshes
      if (!showPrompt) markActive();
    };
    activityKeys.forEach((k) => window.addEventListener(k, handler, { passive: true }));
    return () => activityKeys.forEach((k) => window.removeEventListener(k, handler));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPrompt, isAuthed]);

  const contextValue = useMemo(() => ({
    authData, login, logout, refreshAccess, updateProfile,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [authData]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <TokenExpiryModal
        visible={showPrompt && isAuthed}
        onRefresh={refreshAccess}
        onLogout={logout}
        secondsLeft={msUntilExpiry}
        reason={promptReason}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
